import dayjs from 'dayjs';

/**
 * Periodo e granularità della pagina «Pagine elaborate».
 *
 * Il periodo è un fatto di calendario, non un istante: le date viaggiano verso il backend come
 * `YYYY-MM-DD` e la conversione in istanti la fa il server, nel fuso di reporting
 * dell'installazione. Convertire qui in UTC sposterebbe un giorno di consumi per ogni utente in
 * `Europe/Rome` (un `startOf('month')` convertito diventa l'ultimo giorno del mese precedente).
 */

export const PRESETS = [
  { id: 'month', label: 'Mese' },
  { id: 'd30', label: '30 giorni' },
  { id: 'd90', label: '90 giorni' },
  { id: 'year', label: 'Anno corrente' },
];

export const DEFAULT_PRESET = 'd30';

/** Soglie di leggibilità del grafico: oltre, le barre diventano illeggibili. */
const MAX_DAILY_POINTS = 35;
const MAX_WEEKLY_POINTS = 130;

const API_DATE = 'YYYY-MM-DD';

export const isValidPreset = (preset) => PRESETS.some((p) => p.id === preset);

/** Intervallo del preset, estremi inclusi, in date di calendario locali. */
export const resolvePeriod = (preset, today = dayjs()) => {
  const to = dayjs(today).startOf('day');

  switch (preset) {
    case 'month':
      return { from: to.startOf('month'), to };
    case 'd90':
      return { from: to.subtract(89, 'day'), to };
    case 'year':
      return { from: to.startOf('year'), to };
    case 'd30':
    default:
      return { from: to.subtract(29, 'day'), to };
  }
};

/** Giorni coperti dall'intervallo, estremi inclusi. */
export const lengthInDays = (from, to) => dayjs(to).diff(dayjs(from), 'day') + 1;

/**
 * Periodo di confronto: contiguo e di pari lunghezza, mai «il mese scorso».
 * Il backend calcola lo stesso intervallo; qui serve per le etichette e per il CSV.
 */
export const previousPeriod = (from, to) => {
  const length = lengthInDays(from, to);
  const previousTo = dayjs(from).subtract(1, 'day');
  return { from: previousTo.subtract(length - 1, 'day'), to: previousTo };
};

/**
 * Granularità dedotta dall'ampiezza. È una decisione di rendering — quanti punti la barra riesce a
 * disegnare — quindi sta con chi disegna. Il backend la valida e restituisce quella applicata: è
 * quella della risposta che va usata per etichettare il grafico.
 */
export const resolveGranularity = (from, to) => {
  const days = lengthInDays(from, to);
  if (days <= MAX_DAILY_POINTS) return 'day';
  if (days <= MAX_WEEKLY_POINTS) return 'week';
  return 'month';
};

/** Parametri di query per le API di usage. */
export const toApiParams = (from, to, granularity) => ({
  dateFrom: dayjs(from).format(API_DATE),
  dateTo: dayjs(to).format(API_DATE),
  ...(granularity ? { granularity } : {}),
});

/**
 * Variazione percentuale. Con periodo precedente a zero la variazione non esiste: null, non +100%.
 * Il backend la calcola già così; questa resta per il CSV e per i casi in cui il client ricalcola.
 */
export const computeDelta = (current, previous) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) {
    return null;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

/** Titolo del grafico, coerente con la granularità dichiarata dalla risposta. */
export const granularityLabel = (granularity) => {
  switch (String(granularity).toLowerCase()) {
    case 'week':
      return 'per settimana';
    case 'month':
      return 'per mese';
    case 'day':
    default:
      return 'per giorno';
  }
};
